import {
  clipTextFromTag,
  extractClipTagFromText,
  isClipSegmentText,
} from './clipSegmentUtils';
import { mergeClipsTree, mergeSectionVideos } from './bulkLinkImportUtils';
import { normalizeEditHintsForClient } from './editHintsUtils';

function clipTagsFromSources(sources) {
  const tags = new Set();
  (sources || []).forEach(src => {
    (src.vidclips || []).forEach(clip => {
      const tag = (clip.tag || '').trim();
      if (tag) tags.add(tag);
    });
  });
  return tags;
}

function normalizeSegment(seg, defaultCharacter) {
  const character = (seg.character || defaultCharacter || '').trim();
  const text = (seg.text || '').trim();
  const out = { character, text };
  const tag = (seg.tag || '').trim();
  if (tag) out.tag = tag;
  if (isClipSegmentText(text) && !out.tag) {
    out.tag = extractClipTagFromText(text);
  }
  if (out.tag && !isClipSegmentText(text)) {
    out.text = clipTextFromTag(out.tag);
  }
  return out;
}

function sectionScriptFromSegments(segments) {
  return (segments || []).map(s => (s.text || '').trim()).filter(Boolean).join(' ');
}

/** Drop LLM-only fields before validate/apply/persist (refinement, bridgeHints, followsClip). */
export function sanitizeCompositionImportForRuntime(parsed) {
  const meta = parsed?.meta && typeof parsed.meta === 'object' ? parsed.meta : {};
  const runtimeMeta = {};
  const metaKeep = [
    'batchVideo',
    'commentaryId',
    'workingTitle',
    'commentaryUrl',
    'protocol',
    'generatedAt',
    'sourceNote',
    'compositionSection',
  ];
  metaKeep.forEach(k => {
    if (meta[k] != null && meta[k] !== '') runtimeMeta[k] = meta[k];
  });

  let runtimeScript;
  const script = parsed?.script;
  if (script && typeof script === 'object') {
    runtimeScript = {
      ...(script.intro != null ? { intro: script.intro } : {}),
      ...(script.outro != null ? { outro: script.outro } : {}),
      bridges: (script.bridges || []).map(b => ({
        afterTag: b?.afterTag,
        text: b?.text,
      })),
    };
  }

  const out = {
    schemaVersion: parsed.schemaVersion,
    character: parsed.character,
    sources: parsed.sources,
    sections: {
      intro: parsed.sections?.intro,
    },
    ...(Object.keys(runtimeMeta).length ? { meta: runtimeMeta } : {}),
    ...(runtimeScript ? { script: runtimeScript } : {}),
  };
  if (parsed.editHints && typeof parsed.editHints === 'object') {
    out.editHints = normalizeEditHintsForClient(parsed.editHints);
  }
  return out;
}

/**
 * v1 import: entire episode lives on Intro tab only (sections.intro).
 * @returns {{ ok: true, warnings: string[] } | { ok: false, error: string }}
 */
export function validateCompositionImport(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, error: 'Import must be a JSON object.' };
  }
  if (parsed.schemaVersion !== 1) {
    return { ok: false, error: 'Unsupported schemaVersion (expected 1).' };
  }

  const character = (parsed.character || '').trim();
  if (!character) {
    return { ok: false, error: 'Missing character name.' };
  }

  const sources = parsed.sources;
  if (!Array.isArray(sources) || sources.length === 0) {
    return { ok: false, error: 'sources[] must contain at least one URL.' };
  }

  const sourceTags = clipTagsFromSources(sources);
  for (let i = 0; i < sources.length; i += 1) {
    const src = sources[i];
    if (!src?.url || !/^https?:\/\//i.test(String(src.url).trim())) {
      return { ok: false, error: `sources[${i}].url must be a valid http(s) URL.` };
    }
    const clips = src.vidclips;
    if (!Array.isArray(clips) || clips.length === 0) {
      return { ok: false, error: `sources[${i}] needs at least one vidclip.` };
    }
    for (let j = 0; j < clips.length; j += 1) {
      const c = clips[j];
      if (!c.startTime || !c.endTime) {
        return { ok: false, error: `sources[${i}].vidclips[${j}] needs startTime and endTime.` };
      }
      if (!(c.tag || '').trim()) {
        return { ok: false, error: `sources[${i}].vidclips[${j}] missing tag.` };
      }
    }
  }

  const introBlock = parsed.sections?.intro;
  if (!introBlock || !Array.isArray(introBlock.segment)) {
    return { ok: false, error: 'sections.intro.segment is required (full episode timeline).' };
  }

  const introSegs = introBlock.segment;
  for (let i = 0; i < introSegs.length; i += 1) {
    const seg = introSegs[i];
    const text = (seg?.text || '').trim();
    if (isClipSegmentText(text)) {
      const tag = extractClipTagFromText(text) || (seg.tag || '').trim();
      if (!tag || !sourceTags.has(tag)) {
        return {
          ok: false,
          error: `Intro segment ${i + 1} clip tag "${tag || '?'}" has no matching vidclip in sources.`,
        };
      }
    }
  }

  const warnings = [];
  if (parsed.sections?.body?.segment?.length) {
    warnings.push('sections.body is ignored — import applies to Intro tab only.');
  }
  if (parsed.sections?.conclusion?.segment?.length) {
    warnings.push('sections.conclusion is ignored — import applies to Intro tab only.');
  }

  const script = parsed.script || {};
  const bridgeCount = Array.isArray(script.bridges) ? script.bridges.length : 0;
  const clipCount = sourceTags.size;
  if (clipCount > 1 && bridgeCount > 0 && bridgeCount !== clipCount - 1) {
    warnings.push(
      `script.bridges: expected ${clipCount - 1}, found ${bridgeCount} (informational).`,
    );
  }

  return { ok: true, warnings };
}

export function sourcesToIntroVideosTree(sources, character) {
  const videos = (sources || []).map((src, idx) => ({
    url: src.url.trim(),
    title: (src.title || '').trim() || `Source ${idx + 1}`,
    vidclips: (src.vidclips || []).map(clip => ({
      startTime: clip.startTime,
      endTime: clip.endTime,
      mute: clip.mute !== false,
      tag: (clip.tag || '').trim(),
    })),
  }));
  return {
    sectionVideos: mergeSectionVideos([], character, videos),
    clipsTree: mergeClipsTree([], character, videos),
  };
}

/**
 * Apply import to Intro only — returns patches for sections[0] and selectedData.intro.
 */
export function buildIntroImportPatch(compositionJson) {
  const runtime = sanitizeCompositionImportForRuntime(compositionJson);
  const validation = validateCompositionImport(runtime);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const character = runtime.character.trim();
  const segment = (runtime.sections.intro.segment || []).map(s =>
    normalizeSegment(s, character),
  );
  const { sectionVideos, clipsTree } = sourcesToIntroVideosTree(
    runtime.sources,
    character,
  );

  return {
    introSectionPatch: {
      section: 'intro',
      segment,
      section_script: sectionScriptFromSegments(segment),
      section_characters: [character],
      section_videos: sectionVideos,
      section_images: [],
      clips: clipsTree,
      imageData: [],
    },
    selectedIntroPatch: {
      segment,
      section_script: sectionScriptFromSegments(segment),
      section_characters: [character],
      section_videos: sectionVideos,
      clips: clipsTree,
    },
    clipTags: [...clipTagsFromSources(runtime.sources)],
    warnings: validation.warnings || [],
  };
}

/**
 * Mutate Composition state — Intro tab only; body/conclusion untouched.
 */
export function applyCompositionImportToIntro(compositionJson, {
  sections,
  setSections,
  setSelectedData,
}) {
  const runtime = sanitizeCompositionImportForRuntime(compositionJson);
  const { introSectionPatch, selectedIntroPatch, clipTags, warnings } =
    buildIntroImportPatch(compositionJson);

  const introIdx = (sections || []).findIndex(s => s.section === 'intro');
  const idx = introIdx >= 0 ? introIdx : 0;

  if (!sections?.[idx]) {
    throw new Error('Intro section not found in composition.');
  }

  const char = runtime.character.trim();
  const chars = sections[idx].section_characters || [];
  const namedChars = chars.map(c => (c || '').trim()).filter(Boolean);
  if (namedChars.length && !namedChars.includes(char)) {
    throw new Error(
      `Add "${char}" to Intro characters first, then import again.`,
    );
  }

  const introSectionDoc = {
    ...introSectionPatch,
    section_characters: namedChars.includes(char) ? namedChars : [char, ...namedChars],
  };

  setSections(() => [introSectionDoc]);

  setSelectedData(prev => ({
    ...prev,
    intro: {
      ...(prev.intro || {}),
      ...selectedIntroPatch,
      section: 'intro',
    },
  }));

  return {
    clipTags,
    warnings,
    editHints: runtime.editHints || null,
    sectionsForSave: [introSectionDoc],
  };
}

export function tryParseCompositionJson(raw) {
  const text = (raw || '').trim();
  if (!text) return { ok: false, error: 'Paste composition import JSON.' };
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Invalid JSON — check brackets and quotes.' };
  }
  const composition = sanitizeCompositionImportForRuntime(parsed);
  const validation = validateCompositionImport(composition);
  if (!validation.ok) return validation;
  return { ok: true, composition, warnings: validation.warnings || [] };
}

/** Homepage / API: single Intro section only (no body/finale placeholders). */
export function buildSectionsForNewCompositionDoc(compositionJson) {
  const { introSectionPatch } = buildIntroImportPatch(compositionJson);
  return [{ ...introSectionPatch, section: 'intro' }];
}
